import { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X } from 'lucide-react';
import { AnalyticsSurfaceCard } from '@repo/ui/components/cards/AnalyticsSurfaceCard';
import { useDevices } from '../../../hooks/devices/useDevices';
import { useAvailableDevices } from '../../../hooks/devices/useAvailableDevices';
import { DeviceType } from '../../../hooks/devices/types';
import { useOuraRing } from '../../../hooks/devices/oura-ring/useOuraRing';
import { useDisconnectDevice } from '../../../hooks/devices/useDisconnectDevice';
import { DeviceItem } from './DeviceItem';
import { DeviceTypeItem } from './DeviceTypeItem';
import { EmptyDevicesList } from './EmptyDevicesList';
import { ErrorDisplay } from './ErrorDisplay';
import { useQueryClient } from '@tanstack/react-query';
import { OuraHeartRatePopup } from './oura-heart-rate/OuraHeartRatePopup';
import { ComingSoonPopup } from './ComingSoonPopup';
import { palette } from '@repo/ui/theme';

export function DeviceConnections() {
  const queryClient = useQueryClient();
  const { devices, isLoading: devicesLoading, error: devicesError } = useDevices();

  const {
    availableDevices,
    isLoading: availableDevicesLoading,
    error: availableDevicesError,
  } = useAvailableDevices();

  const { connectToOuraRing } = useOuraRing();
  const { disconnect, isDisconnecting } = useDisconnectDevice();
  const [showDeviceTypes, setShowDeviceTypes] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [comingSoonDevice, setComingSoonDevice] = useState<string | null>(null);

  useEffect(() => {
    if (showDeviceTypes) {
      queryClient.invalidateQueries({ queryKey: ['availableDevices'] });
    }
  }, [showDeviceTypes, queryClient]);

  const handleAddDevice = useCallback(
    (deviceType: DeviceType) => {
      if (deviceType === DeviceType.OURA_RING) {
        connectToOuraRing();
      } else {
        setComingSoonDevice(deviceType);
      }
      setShowDeviceTypes(false);
    },
    [connectToOuraRing],
  );

  const handleDisconnectDevice = useCallback(
    async (deviceId: string) => {
      try {
        await disconnect(deviceId);
      } catch (error) {
        console.error('Failed to disconnect device:', error);
      }
    },
    [disconnect],
  );

  const handleDeviceClick = useCallback((deviceId: string) => {
    setSelectedDeviceId(deviceId);
  }, []);

  const closeHeartRatePopup = () => {
    setSelectedDeviceId(null);
  };

  const closeComingSoonPopup = () => {
    setComingSoonDevice(null);
  };

  const toggleDeviceTypes = () => {
    setShowDeviceTypes(prev => !prev);
  };

  const isLoading = devicesLoading || availableDevicesLoading;
  const error = devicesError || availableDevicesError;

  const pageTransition = useMemo(
    () => ({
      initial: (direction: boolean) => ({
        opacity: 0,
        x: direction ? 20 : -20,
      }),
      animate: { opacity: 1, x: 0 },
      exit: (direction: boolean) => ({ opacity: 0, x: direction ? -20 : 20 }),
      transition: { duration: 0.3 },
    }),
    [],
  );

  if (isLoading) {
    return (
      <AnalyticsSurfaceCard
        className="mx-auto w-full max-w-md"
        accentColor={palette.cyan}
        header={
          <div className="text-lg font-semibold text-[var(--anl-text)]">Connected Devices</div>
        }
      >
        <div className="flex justify-center p-4" aria-live="polite" aria-busy="true">
          <div className="animate-pulse text-[var(--anl-text)]">Loading devices...</div>
        </div>
      </AnalyticsSurfaceCard>
    );
  }

  if (error) {
    return (
      <AnalyticsSurfaceCard
        className="mx-auto w-full max-w-md"
        accentColor={palette.cyan}
        header={
          <div className="text-lg font-semibold text-[var(--anl-text)]">Connected Devices</div>
        }
      >
        <ErrorDisplay message={error.message} />
      </AnalyticsSurfaceCard>
    );
  }

  return (
    <>
      <AnalyticsSurfaceCard
        className="mx-auto h-full w-full max-w-md overflow-hidden lg:max-w-lg"
        accentColor={palette.cyan}
        header={
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold text-[var(--anl-text)]">
              {showDeviceTypes ? 'Connect a Device' : 'Connected Devices'}
            </div>
            <button
              onClick={toggleDeviceTypes}
              className="text-[var(--anl-text)] transition-colors hover:text-[var(--anl-text-muted)]"
              aria-label={showDeviceTypes ? 'Close device types' : 'Add device'}
            >
              {showDeviceTypes ? (
                <X className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Plus className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        }
      >
        <AnimatePresence mode="wait" custom={showDeviceTypes}>
          <motion.div
            key={showDeviceTypes ? 'device-types' : 'connected-devices'}
            custom={showDeviceTypes}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageTransition}
            className="h-full"
            aria-live="polite"
          >
            {!showDeviceTypes ? (
              <div className="flex flex-col">
                {devices.length === 0 ? (
                  <EmptyDevicesList onAddDevice={() => setShowDeviceTypes(true)} />
                ) : (
                  <div className="flex flex-col" role="list" aria-label="Connected devices">
                    {devices.map(device => (
                      <DeviceItem
                        key={device.deviceid}
                        device={device}
                        isDisconnecting={isDisconnecting}
                        onDisconnect={handleDisconnectDevice}
                        onClick={handleDeviceClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3 p-1">
                {availableDevices.length === 0 ? (
                  <div className="py-6 text-center text-[var(--anl-text-muted)]">
                    No available devices
                  </div>
                ) : (
                  <div
                    className="flex flex-col gap-2"
                    role="list"
                    aria-label="Available device types"
                  >
                    {availableDevices.map(type => (
                      <DeviceTypeItem
                        key={type.deviceid}
                        type={type}
                        onAddDevice={handleAddDevice}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </AnalyticsSurfaceCard>

      <AnimatePresence>
        {selectedDeviceId && <OuraHeartRatePopup onClose={closeHeartRatePopup} />}
      </AnimatePresence>

      <AnimatePresence>
        {comingSoonDevice && (
          <ComingSoonPopup deviceName={comingSoonDevice} onClose={closeComingSoonPopup} />
        )}
      </AnimatePresence>
    </>
  );
}
